import React from 'react'

import {
  ArrowLeft,
  ArrowRight,
  ArrowToLeft,
  ArrowToRight,
  Icon,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, number, string } from 'prop-types'

import WithTriangleUiContext from 'Pods/Shared/Hocs/WithTriangleUiContext'
import i18n from 'Pods/Shared/I18n'
import buildPageHref from 'Pods/Shared/Utils/buildPageHref'

import PaginationLink from './PaginationLink'
import { useCrawlingTags } from '../../useCrawlingTags'

const VISIBLE_PAGES = 5

function visiblePages(currentPage, totalPages) {
  const half = Math.floor(VISIBLE_PAGES / 2)
  const start = Math.max(1, Math.min(currentPage - half, totalPages - VISIBLE_PAGES + 1))
  const end = Math.min(totalPages, start + VISIBLE_PAGES - 1)

  return Array.from({ length: end - start + 1 }, (_, index) => start + index)
}

function isModifiedClick(event) {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0
}

function PaginationLinks({
  isEnabled,
  baseUrl,
  currentPage,
  totalPages,
  setSelectedFilters,
  shouldSwapCrawlingTags,
  keywords = '',
  category = '',
  locale = 'en',
}) {
  const t = (key, options) => i18n.t(`listingPage.pagination.${key}`, { ns: 'rails', lng: locale, ...options })
  const {
    hasCanonicalTag,
    hasRobotsTag,
    addCanonicalTag,
    removeCanonicalTag,
    addRobotsTag,
    removeRobotsTag,
    adjustCanonicalTag,
  } = useCrawlingTags(shouldSwapCrawlingTags)

  if (!isEnabled || totalPages < 2) {
    return null
  }

  const page = Number(currentPage)
  const isFirst = page <= 1
  const isLast = page >= totalPages

  const syncCrawlingTags = (targetPage) => {
    const canonicalHref = window.location.toString().replace(window.location.search, '')

    if (!shouldSwapCrawlingTags) {
      adjustCanonicalTag(`${canonicalHref}?page=${targetPage}`)
      return
    }

    if (targetPage === 1) {
      if (!hasCanonicalTag) addCanonicalTag(canonicalHref)
      if (hasRobotsTag) removeRobotsTag()
      return
    }

    if (hasCanonicalTag) removeCanonicalTag()
    if (!hasRobotsTag) addRobotsTag()
  }

  const handleClick = (event, targetPage) => {
    if (isModifiedClick(event)) {
      return
    }

    event.preventDefault()
    syncCrawlingTags(targetPage)

    window.customEventTracker?.({
      name: 'proceed_to_page',
      customAttributes: { event_source: 'pagination links' },
      eventData: {
        page_number: `${targetPage}`,
        search_term: keywords || category || '',
      },
    })

    setSelectedFilters({ name: 'page', value: targetPage })
  }

  const renderControl = ({ targetPage, label, svgComponent, isDisabled }) => (
    <PaginationLink
      page={targetPage}
      href={buildPageHref(baseUrl, targetPage)}
      label={label}
      onClick={handleClick}
      isDisabled={isDisabled}
    >
      <Icon svgComponent={svgComponent} />
    </PaginationLink>
  )

  return (
    <nav className="listing-pagination-links" aria-label={t('label')}>
      <ul className="listing-pagination-links__list">
        {renderControl({
          targetPage: 1,
          label: t('firstPage'),
          svgComponent: ArrowToLeft,
          isDisabled: isFirst,
        })}
        {renderControl({
          targetPage: page - 1,
          label: t('previousPage'),
          svgComponent: ArrowLeft,
          isDisabled: isFirst,
        })}
        {visiblePages(page, totalPages).map((pageNumber) => (
          <PaginationLink
            key={pageNumber}
            page={pageNumber}
            href={buildPageHref(baseUrl, pageNumber)}
            label={t('page', { number: pageNumber })}
            onClick={handleClick}
            isCurrent={pageNumber === page}
          >
            {pageNumber}
          </PaginationLink>
        ))}
        {renderControl({
          targetPage: page + 1,
          label: t('nextPage'),
          svgComponent: ArrowRight,
          isDisabled: isLast,
        })}
        {renderControl({
          targetPage: totalPages,
          label: t('lastPage'),
          svgComponent: ArrowToRight,
          isDisabled: isLast,
        })}
      </ul>
    </nav>
  )
}

PaginationLinks.propTypes = {
  isEnabled: bool.isRequired,
  baseUrl: string.isRequired,
  currentPage: number.isRequired,
  totalPages: number.isRequired,
  setSelectedFilters: func.isRequired,
  shouldSwapCrawlingTags: bool.isRequired,
  keywords: string,
  category: string,
  locale: string,
}

const mapStateToProps = ({
  printModelsListing,
  paginationBaseUrl,
  currentMeta: { currentPage, totalPages },
  setSelectedFilters,
  shouldSwapCrawlingTags,
  selectedFilters: { keywords },
  pageCategory,
  locale,
}) => ({
  isEnabled: !!printModelsListing,
  baseUrl: paginationBaseUrl || '',
  currentPage: Number(currentPage),
  totalPages: Number(totalPages),
  setSelectedFilters,
  shouldSwapCrawlingTags,
  keywords,
  category: pageCategory,
  locale,
})

export default WithTriangleUiContext(mapStateToProps)(PaginationLinks)
